/* @flow */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { autorun, observable } from 'mobx';
import { translate } from 'react-i18next';
import { toJS } from 'mobx';
import Loading from 'components/Loading/Loading';

import type TypeAccountStore from 'stores/AccountStore.js';
import FinderWrapper from 'components/FinderWrapper/FinderWrapper';
import FinderGrid from 'components/FinderGrid/FinderGrid';
import PageSearch from 'components/PageSearch';
import { Roles } from 'permissions/defs';

import AccountUsersList from './AccountUsersList/AccountUsersList';
import ActionDrawerWrapper from '../../../../components/ActionDrawerWrapper/ActionDrawerWrapper';
import {
  ChangeUserRole,
  DisableAccess,
  EnableAccess,
  LoginAsUser,
  RemoveUserAccess,
  RevokeAccess,
  StartAuditByUsers,
  ViewAsUser,
} from '../../../ActionDrawer';

type TypeProps = {|
  accountStore: TypeAccountStore,
|};

@translate()
@inject('accountStore', 'actionDrawerStore', 'reportViewStore')
@observer
export default class AccountUsers extends React.Component<*> {
  @observable searchTerm = '';

  componentDidMount() {
    const accountStore = this.props.accountStore;

    const accountId = this.props.id;

    if (accountStore.users.length === 0 || accountStore.accountId !== accountId) {
      accountStore.loadUsers(accountId);
    }

    autorun(() => {
      if (this.searchTerm) {
        accountStore.loadUsers(accountId, [this.searchTerm]);
      } else {
        accountStore.loadUsers(accountId, []);
      }
    });
  }

  render() {
    const { accountStore, actionDrawerStore, t }: TypeProps = this.props;

    const { allUsersByAccount }: { allUsersByAccount: any } = accountStore;

    if (accountStore.loadingUsers || !allUsersByAccount) {
      return <Loading />;
    }

    // Get all users assigned to that trading account (grouped by roles)
    const allUsersByAccountArray: Array<Object> = toJS(allUsersByAccount);

    const downloadShouldBeDisabled = Object.keys(allUsersByAccountArray).length === 0;

    return (
      <React.Fragment>
        <PageSearch
          viewConfig={{
            codename: 'account-users',
            title: '',
            type: 'SINGLE',
            userFilterNamespace: '',
            view: { columns: [], dataPathname: '' },
            disableFilters: true,
            disableDownload: downloadShouldBeDisabled,
          }}
          onDownloadClick={() => {
            this.props.actionDrawerStore.getUrlAsCsvDownload(
              this.props.reportViewStore.formulateUrl(
                `SQL`,
                `accounts/${this.props.id}/users/export/csv`,
                {
                  search: this.searchTerm || undefined,
                }
              ),
              'account-users'
            );
          }}
          onSearchSubmit={newSearchTerm => {
            this.searchTerm = newSearchTerm;
          }}
          onClearSearch={() => {
            this.searchTerm = '';
          }}
          searchTerm={this.searchTerm}
        />
        <FinderWrapper>
          <FinderGrid>
            <AccountUsersList
              inviteUserRole={Roles.ACCOUNT_ADMIN}
              addNewTitle={t(`general:actions.add_new_user_of_role.${Roles.ACCOUNT_ADMIN}`)}
              statusTitle={t(`general:roles.${Roles.ACCOUNT_ADMIN}_plural`)}
              tooltip=""
              finderColumn={{
                columnTitle: t(`general:roles.${Roles.ACCOUNT_ADMIN}_plural`),
                columnData: accountStore.admins,
                displayCount: true,
              }}
            />
            <AccountUsersList
              inviteUserRole={Roles.MANAGER}
              addNewTitle={t(`general:actions.add_new_user_of_role.${Roles.MANAGER}`)}
              statusTitle={t(`general:roles.${Roles.MANAGER}_plural`)}
              tooltip=""
              finderColumn={{
                columnTitle: t(`general:roles.${Roles.MANAGER}_plural`),
                disableAddNewButton: accountStore.admins.length === 0,
                tooltip: {
                  disabled_add_new_button: t(
                    'general:actions.add_new_user_tooltip.disabled_add_new_button'
                  ),
                },
                columnData: accountStore.managers,
                displayCount: true,
              }}
            />
            <AccountUsersList
              inviteUserRole={Roles.PURCHASER}
              addNewTitle={t(`general:actions.add_new_user_of_role.${Roles.PURCHASER}`)}
              statusTitle={t(`general:roles.${Roles.PURCHASER}_plural`)}
              tooltip=""
              finderColumn={{
                columnTitle: t(`general:roles.${Roles.PURCHASER}_plural`),
                disableAddNewButton: accountStore.admins.length === 0,
                tooltip: {
                  disabled_add_new_button: t(
                    'general:actions.add_new_user_tooltip.disabled_add_new_button'
                  ),
                },
                columnData: accountStore.purchasers,
                displayCount: true,
              }}
            />
            <AccountUsersList
              inviteUserRole={Roles.USER}
              addNewTitle={t(`general:actions.add_new_user_of_role.${Roles.USER}`)}
              statusTitle={t(`general:roles.${Roles.USER}_plural`)}
              tooltip=""
              finderColumn={{
                columnTitle: t(`general:roles.${Roles.USER}_plural`),
                disableAddNewButton: accountStore.admins.length === 0,
                tooltip: {
                  disabled_add_new_button: t(
                    'general:actions.add_new_user_tooltip.disabled_add_new_button'
                  ),
                },
                columnData: accountStore.users,
                displayCount: true,
              }}
            />
            <AccountUsersList
              inviteUserRole={Roles.VIEW_ONLY}
              addNewTitle={t(`general:actions.add_new_user_of_role.${Roles.VIEW_ONLY}`)}
              statusTitle={t(`general:roles.${Roles.VIEW_ONLY}_plural`)}
              tooltip=""
              finderColumn={{
                columnTitle: t(`general:roles.${Roles.VIEW_ONLY}_plural`),
                disableAddNewButton: accountStore.admins.length === 0,
                tooltip: {
                  disabled_add_new_button: t(
                    'general:actions.add_new_user_tooltip.disabled_add_new_button'
                  ),
                },
                columnData: accountStore.customerViewOnly,
                displayCount: true,
              }}
            />
          </FinderGrid>
        </FinderWrapper>
        <ActionDrawerWrapper
          appendActionToURL={true}
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              removeUser: <RemoveUserAccess currentAccountDetails={accountStore.details.details} />,
              startAuditByUsers: (
                <StartAuditByUsers user={actionDrawerStore.currentActionDrawerData} type="users" />
              ),
              changeUserRole: (
                <ChangeUserRole
                  formData={actionDrawerStore.currentActionDrawerData}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
              viewAsUser: (
                <ViewAsUser
                  formData={actionDrawerStore.currentActionDrawerData}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
              loginAsUser: (
                <LoginAsUser
                  formData={actionDrawerStore.currentActionDrawerData}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
              disableUserAccess: (
                <DisableAccess
                  formData={actionDrawerStore.currentActionDrawerData}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
              enableUserAccess: (
                <EnableAccess
                  formData={actionDrawerStore.currentActionDrawerData}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
              revokeUserAccess: (
                <RevokeAccess
                  formData={actionDrawerStore.currentActionDrawerData}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
            }[actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}
